import PropTypes from 'prop-types';
// CORE
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { rootUiActions } from 'app-components/flux/root-ui';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
} from 'app-components/flux/scheduled-transactions';

import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// Data
import { useDispatch, useSelector } from 'react-redux';

// CUSTOM
import { getLogger } from 'app-components/utils/core';
import RecurringPage from 'components/GettingStartedAcme/Recurring/RecurringPage';
import QCheck from 'components/QCheck';
import StdDialog from '../StdDialog';
import StdDialogActions from '../StdDialogActions';
import BillScoutLoadingView from './BillScoutLoadingView';
import { DIALOG_TYPE } from './actions';

const log = getLogger('BillScoutPresentPage');

const useStyles = makeStyles()(() => ({
  root: {
    width: 768,
  },
  contentRoot: {
    margin: '32px 24px 24px',
    height: 359,
  },

  // Recurring stuff below
  recurringList: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '4px',
  },
  centerWrapper: {
    height: 415,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
}));

const BillScoutDialog = (props) => {
  const { classes } = useStyles();
  const { institutionLoginId, accountClientIds } = props;
  const dispatch = useDispatch();

  const accounts = useSelector((state) =>
    accountsSelectors.getAccountsForInstitutionLoginId(state, { institutionLoginId }),
  );

  // find accounts by recently added clientIds (will disappear after refresh)
  const filteredAccountIds = useMemo(() => {
    let ret = accounts;
    if (accountClientIds && accountClientIds.length) {
      ret = accounts.filter((account) => account?.id && accountClientIds.includes(account.clientId));
    }
    return ret.map((account) => account.id).toArray();
  }, [accountClientIds, accounts]);

  const [accountSnapshot, setAccountSnapshot] = useState([]);

  // use account snapshot if available to get scheduledTransactions
  const { income, bills } = useSelector((state) =>
    scheduledTransactionsSelectors.getAccountFilteredBillsAndIncome(
      state,
      accountSnapshot?.length ? accountSnapshot : filteredAccountIds,
    ),
  );

  const scheduleLoading = useSelector(scheduledTransactionsSelectors.getIsLoading);
  const institutionsRefreshing = useSelector(institutionLoginsSelectors.getIdsRefreshing);
  const banksLoading = !institutionsRefreshing.isEmpty();

  const dataRef = useRef();
  const autoClose = useRef();
  const autoProceed = useRef();

  const [loading, setLoading] = useState('agg');
  const [state, setState] = useState('waiter');

  // snapshot accounts when they still have clientIds, because clientIds are cleared after a refresh
  useEffect(() => {
    if (filteredAccountIds && filteredAccountIds.length > accountSnapshot?.length) {
      setAccountSnapshot(filteredAccountIds);
    }
  }, [filteredAccountIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                       Navigation functions and UI logic                         //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  const onClose = useCallback(() => {
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE));
  }, [dispatch]);

  function doAutoProceed() {
    const { current } = dataRef;
    if (current.state === 'waiter') {
      // still on waiter, so proceed
      // decide which page to proceed to
      if (current.showIncome) {
        setState('income');
      } else if (current.showBills) {
        setState('bills');
      } else {
        setState('final');
      }
    }
  }

  let showIncome = false;
  let showBills = false;
  if (!loading) {
    showIncome = income.size > 0;
    showBills = bills.size > 0;

    // capture state values in ref
    dataRef.current = { showIncome, showBills, state };
    // auto-close if no models
    if (!showIncome && !showBills) {
      log.info('No models found, closing dialog');
      if (!autoClose.current) {
        autoClose.current = setTimeout(onClose, 800);
      }
    } else if (state === 'waiter' && !autoProceed.current) {
      // auto-proceed from the waiting screen after models are in
      autoProceed.current = setTimeout(doAutoProceed, 1800);
    }
  }

  // make sure everything loads in the right order
  useEffect(() => {
    if (loading === 'agg' && !banksLoading) {
      if (scheduleLoading) {
        setLoading('ST');
      } else {
        setTimeout(() => dispatch(scheduledTransactionsActions.getScheduledTransactions()), 1800);
      }
    }
    if (loading === 'ST' && !scheduleLoading) {
      setLoading(false);
    }
  }, [loading, banksLoading, scheduleLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // cleanup timeouts on unmount
  useEffect(
    () => () => {
      if (autoProceed.current) clearTimeout(autoProceed);
      if (autoClose.current) clearTimeout(autoClose);
    },
    [],
  );

  const proceed = () => {
    switch (state) {
      case 'waiter':
        if (showIncome) {
          setState('income');
        } else if (showBills) {
          setState('bills');
        } else {
          setState('final');
        }
        break;
      case 'income':
        if (showBills) {
          setState('bills');
        } else {
          setState('final');
        }
        break;
      case 'bills':
        setState('final');
        break;
      case 'final':
        onClose();
        break;
      default:
        setState('final');
    }
  };

  const onBack = () => {
    switch (state) {
      case 'income':
        setState('waiter');
        break;
      case 'bills':
        if (showIncome) {
          setState('income');
        } else {
          setState('waiter');
        }
        break;
      case 'final':
        if (showBills) {
          setState('bills');
        } else if (showIncome) {
          setState('income');
        } else {
          setState('waiter');
        }
        break;
      default:
        setState('waiter');
    }
  };

  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                                  Renderers                                      //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //

  const renderIncome = () => (
    <div key='Income_Page' className={classes.contentRoot}>
      <RecurringPage key='Income' scheduledTxns={income} institutionLoginId={institutionLoginId} />
    </div>
  );

  const renderBills = () => (
    <div key='Bills_Page' className={classes.contentRoot}>
      <RecurringPage key='Bills' scheduledTxns={bills} isBill institutionLoginId={institutionLoginId} />
    </div>
  );

  const renderFinalRedirect = () => (
    <div className={classes.centerWrapper}>
      <Typography variant='titleMedium'>{"You're all set!"}</Typography>
      <div className={classes.recurringList}>
        <QCheck style={{ margin: 16 }} size={44} />
      </div>
    </div>
  );

  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                                   Main Return                                   //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  return (
    <StdDialog
      onBack={onBack}
      onClose={onClose}
      open
      sharedcomponentid={'BILL_SCOUT_DIALOG'}
      showBackButton={state !== 'waiter'}
      size='md'
      title='Confirm recurring income and bills'
    >
      <div className={classes.root}>
        {state === 'waiter' && <BillScoutLoadingView loading={Boolean(loading)} />}
        {state === 'income' && renderIncome()}
        {state === 'bills' && renderBills()}
        {state === 'final' && renderFinalRedirect()}
      </div>
      <StdDialogActions
        primary={{ children: 'Continue', onClick: proceed, variant: 'contained', disabled: Boolean(loading) }}
        secondary={
          state === 'waiter'
            ? {
                children: 'Skip',
                onClick: onClose,
                variant: 'outlined',
              }
            : undefined
        }
      />
    </StdDialog>
  );
};

BillScoutDialog.defaultProps = {};

BillScoutDialog.propTypes = {
  institutionLoginId: PropTypes.string,
  accountClientIds: PropTypes.array,
};

export default BillScoutDialog;
